import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Account } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import './purchase.css';

export interface CreditorDefaults {
  name: string;
  street: string;
  postalCode: string;
  city: string;
  email: string;
  vatId: string;
  iban: string;
  bic: string;
}

/**
 * Findet Wirkia den Lieferanten einer Eingangsrechnung nicht in den Kontakten, legt man hier einen neuen Kreditor an (KON-161): Kontakt, Finanzdaten und Konto in einem
 * Schritt. Die Felder sind aus der Rechnung vorbelegt (Vorschläge aus dem Beleg, gelesene E-Rechnung oder was Sie schon eingetragen haben) und bleiben änderbar.
 */
export function NewCreditorPanel({ defaults, onCreated }: { defaults: CreditorDefaults; onCreated: (account: Account) => void }) {
  const { can } = useSession();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CreditorDefaults>(defaults);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof CreditorDefaults>(key: K, value: string) => setForm((f) => ({ ...f, [key]: value }));

  const mayCreate = can('contacts.finance') && can('contacts.write');
  const save = useMutation({
    mutationFn: async () => {
      const finance = Object.fromEntries(Object.entries({ vatId: form.vatId.trim(), iban: form.iban.trim(), bic: form.bic.trim() }).filter(([, v]) => v));
      return (
        await api<{ data: Account }>('POST', '/creditors', {
          contact: { kind: 'company', name: form.name.trim(), street: form.street.trim(), postalCode: form.postalCode.trim(), city: form.city.trim(), email: form.email.trim() },
          ...(Object.keys(finance).length > 0 ? { finance } : {}),
        })
      ).data;
    },
    onSuccess: (account) => {
      client.invalidateQueries({ queryKey: ['accounts', 'creditor'] });
      client.invalidateQueries({ queryKey: ['contacts'] });
      setOpen(false);
      onCreated(account);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setError('Bitte prüfen Sie die markierten Felder.');
      } else setError(e instanceof ApiError ? e.message : 'Der Kreditor konnte nicht angelegt werden.');
    },
  });

  if (!mayCreate) {
    return <small className="muted">Der Lieferant ist noch kein Kontakt. Zum Anlegen brauchen Sie die Rechte „Kontakte ändern“ und „Finanzdaten der Kontakte“. Bitten Sie jemanden mit diesen Rechten, ihn anzulegen.</small>;
  }
  if (!open) {
    return (
      <div className="creditor-new__toggle">
        <small className="muted">Lieferant nicht in den Kontakten?</small>{' '}
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setForm(defaults); setError(null); setErrors({}); setOpen(true); }}>Neuen Kreditor anlegen</button>
      </div>
    );
  }

  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setErrors({});
    save.mutate();
  }

  return (
    <div className="creditor-new" role="group" aria-label="Neuen Kreditor anlegen">
      <h3>Neuen Kreditor anlegen</h3>
      <p className="muted small">Wirkia legt den Lieferanten in den Kontakten an und gibt ihm eine Kreditorennummer. Die Angaben sind aus der Rechnung vorbelegt, prüfen Sie sie.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field field--wide">
          <span>Name</span>
          <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={120} aria-invalid={!!errors['contact.name']} />
          {err('contact.name')}
        </label>
        <label className="field field--wide">
          <span>Straße und Hausnummer</span>
          <input className="input" value={form.street} onChange={(e) => set('street', e.target.value)} maxLength={120} />
        </label>
        <label className="field">
          <span>PLZ</span>
          <input className="input" value={form.postalCode} onChange={(e) => set('postalCode', e.target.value)} maxLength={10} />
        </label>
        <label className="field">
          <span>Ort</span>
          <input className="input" value={form.city} onChange={(e) => set('city', e.target.value)} maxLength={80} />
        </label>
        <label className="field">
          <span>E-Mail</span>
          <input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} maxLength={200} aria-invalid={!!errors['contact.email']} />
          {err('contact.email')}
        </label>
        <label className="field">
          <span>USt-IdNr.</span>
          <input className="input" value={form.vatId} onChange={(e) => set('vatId', e.target.value)} aria-invalid={!!errors['finance.vatId']} />
          {err('finance.vatId')}
        </label>
        <label className="field">
          <span>IBAN</span>
          <input className="input" value={form.iban} onChange={(e) => set('iban', e.target.value)} aria-invalid={!!errors['finance.iban']} />
          {err('finance.iban')}
        </label>
        <label className="field">
          <span>BIC</span>
          <input className="input" value={form.bic} onChange={(e) => set('bic', e.target.value)} maxLength={11} aria-invalid={!!errors['finance.bic']} />
          {err('finance.bic')}
        </label>
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn--primary btn--sm" disabled={save.isPending || !form.name.trim()} onClick={(e) => submit(e as unknown as FormEvent)}>{save.isPending ? 'Wird angelegt …' : 'Kreditor anlegen'}</button>
        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(false)}>Abbrechen</button>
      </div>
    </div>
  );
}
